/*
 * File: StoryComponent.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 8:44 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useContext, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utilities
import { formatNumber } from '../../../utilities/utilities';
import { generateTitlePlaceholder } from '../../utilities/placeholder';

// Services
import { recordReaction, removeReaction } from '../../services/reaction';

// Store
import { updateUser } from '../../store/slices/user/user.slice';
import { toggleReactionAnimation } from '../../store/slices/animation/animation.slice';

// Context
import ActionContext from '../../context/SharedActionContext';
import AnimationContextBuilder from '../../context/AnimationContext';

// Components
import { LocaleLink } from '../LocaleLink';
import { Badge } from '../Badge';
import { Typography } from '../Typography';
import { ContentLocalityBadge } from '../ContentLocalityBadge';
import { CommentCreator } from '../CommentCreator';
import { CommentFeed } from '../CommentFeed';
import { CardOptionsWidget } from '../CardOptionsWidget';
import { ActionModule } from '../ActionModule';
import { ToolTip } from '../ToolTip';
import { ObfuscatedText } from '../ObfuscatedText';
import { StoryContent } from './StoryContent';
import { FlyToTargetContext } from '../FlyToTargetContext';

// Constants
import { AVAILABLE_I18N_NAMESPACES, REACTIONS, SOURCES } from '../../../constants/base.constants';

// Styles
import * as S from './StoryComponent.styles';


/**
 * Helpers
 */

const generateStoryUpdatePeriod = (diff, t) => {
	if (diff < 2 * 60 * 1000) { // 2 minutes later
		return t('timePeriods.fewMinutesLater');
	} if (diff < 60 * 60 * 1000) { // 1 hour later
		return t('timePeriods.fewMinutesLater');
	} if (diff < 24 * 60 * 60 * 1000) { // 1 day later
		return t('timePeriods.fewHoursLater');
	} if (diff < 7 * 24 * 60 * 60 * 1000) { // 1 week later
		return t('timePeriods.fewDaysLater');
	} if (diff < 2 * 7 * 24 * 60 * 60 * 1000) { // 2 weeks later
		return t('timePeriods.weekLater');
	} if (diff < 30 * 24 * 60 * 60 * 1000) { // 30 days later
		return t('timePeriods.fewWeeksLater');
	} if (diff < 365 * 24 * 60 * 60 * 1000) { // 12 months later
		const numMonths = Math.round(diff / (30 * 24 * 60 * 60 * 1000));
		return numMonths === 1
			? t('timePeriods.monthLater')
			: t('timePeriods.monthsLater', { number: numMonths });
	}
	const numYears = Math.round(diff / (365 * 24 * 60 * 60 * 1000));
	return numYears === 1
		? t('timePeriods.yearLater')
		: t('timePeriods.yearsLater', { number: numYears });
};


/**
 * Component
 */

export const StoryComponent = ({
	className, story, isLoading, showBackButton, navigationBar
}) => {

	// Get context from navigation parent
	const {
		dispatch: actionDispatch
	} = useContext(ActionContext);

	// Get context from navigation parent
	const {
		state: animationState
	} = useContext(AnimationContextBuilder);

	// Get current user from hook
	const currentUser = useSelector((state) => state.user.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get reaction
	const hasReaction = useMemo(() => {

		// Get reaction schema
		const reactionSchema = currentUser.reaction_schema || {};

		// Check for active reaction
		return reactionSchema[story?.story_id] && reactionSchema[story?.story_id].includes(REACTIONS.reaction_a.code);

	}, [currentUser, story]);

	// Get unseen updates
	const unseenUpdates = useMemo(() => {

		// Get updates schema
		const updatesSchema = currentUser.update_schema || {};

		// Check for active reaction
		const seenUpdates = updatesSchema[story?.story_id] || [];

		// Return unseen updates
		return (story?.updates || []).filter(({ update_id: updateId }) => !seenUpdates.includes(updateId)).map((u) => u.update_id);

	}, [currentUser, story]);

	// Create state
	const [isReactionActive, setIsReactionActive] = useState(hasReaction === true);
	const [pendingComments, setPendingComments] = useState([]);
	const [animationDuration, setAnimationDuration] = useState(2.6);

	// Use hooks
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	// Create refs for elements
	const reactionActionModuleRef = useRef();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Generate placeholders
	const titlePlaceholder = useMemo(() => generateTitlePlaceholder(), []);

	// Build title object
	const title = useMemo(() => (story ? `${!story?.title ? `#${story?.number}` : story.title}` : ''), [story]);

	// Handle report story
	const reportStory = () => {

		// Open report modal
		actionDispatch({
			type: 'OPEN_REPORT_MODAL',
			payload: {
				source: SOURCES.STORY,
				sourceId: story?.story_id
			}
		});
	};

	// Handle story reaction
	const handleReaction = async () => {

		// Handle action for state
		if (!isReactionActive) {

			// Update state
			setIsReactionActive(true);

			// Trigger reaction animation
			setTimeout(() => {
				dispatch(toggleReactionAnimation(true));
				setTimeout(() => {
					dispatch(toggleReactionAnimation(false));
				}, 800);
			}, animationDuration * 1000);

			// Record reaction
			const { data: { result: { user } } } = await recordReaction({
				source: SOURCES.STORY,
				entityType: SOURCES.STORY,
				entityId: story?.story_id,
				code: REACTIONS.reaction_a.code,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}

		} else {

			// Update state
			setIsReactionActive(false);

			// Remove reaction
			const { data: { result: { user } } } = await removeReaction({
				source: SOURCES.STORY,
				entityType: SOURCES.STORY,
				entityId: story?.story_id,
				code: REACTIONS.reaction_a.code,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}
		}
	};

	// Handle actions on reaction state change
	useEffect(() => {

		// Update reaction active
		setIsReactionActive(hasReaction === true);

	}, [hasReaction]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className}>

			{/* Card Header */}
			<S.CardHeader>

				{/* Back Button */}
				{showBackButton && <S.BackButton icon={['far', 'arrow-left']} size={1} onClick={() => { navigate(location?.state?.back?.path || `${locale.localePath}`); }} />}

				{/* Badges */}
				<S.BadgeContainer>

					{/* Partner Badge */}
					{story?.partner && partner?.id && story?.partner.id !== partner?.id && (
						<LocaleLink to={`https://${story?.partner.full_domain}`} target="_blank">
							<Badge type="neutral" variation="square" size="small" className="rightIcon">
								<Typography weight="medium">
									<Trans t={t} i18nKey="communityBadge">
										{{ partnerName: story?.partner.name }}
										{' '}
										Community
									</Trans>
								</Typography>
								<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
							</Badge>
						</LocaleLink>
					)}

					{/* Updates Badge */}
					{unseenUpdates.length > 0 && (
						<LocaleLink to={`/story/${story.link_component}#updates`}>
							<Badge type="neutral" variation="square" size="small" className="leftIcon">
								<FontAwesomeIcon icon={['far', 'clock']} />
								<Typography weight="medium">
									{unseenUpdates.length === 1
										? (
											<Trans t={t} i18nKey="storyCard.newUpdateSingle">
												{{ updateCount: unseenUpdates.length }}
												{' '}
												new update
											</Trans>
										) : (
											<Trans t={t} i18nKey="storyCard.newUpdateMultiple">
												{{ updateCount: unseenUpdates.length }}
												{' '}
												new updates
											</Trans>
										)}
								</Typography>
							</Badge>
						</LocaleLink>
					)}

				</S.BadgeContainer>

				{/* Spacer */}
				<S.Spacer />

				{/* Content Locality Badge */}
				{story && <ContentLocalityBadge country={story.submitted_country} id={story?.story_id} />}

			</S.CardHeader>

			{/* Prompt Container */}
			<S.PromptContainer>

				{/* Message Type */}
				<Typography tag="h5" weight="bold" className={isLoading && 'shimmer shimmer-element'}>
					<Trans t={t} i18nKey="storyCard.survivorStory">
						Survivor story
					</Trans>
				</Typography>

			</S.PromptContainer>

			{/* Title */}
			<S.StoryContainer>
				<Typography
					tag="h1"
					tagStyle="h4"
					weight="bold"
					style={{ margin: '0px 0px 8px' }}
					className={isLoading && 'shimmer shimmer-element'}
				>
					<ObfuscatedText>{title || titlePlaceholder}</ObfuscatedText>
				</Typography>
			</S.StoryContainer>

			{/* Updates */}
			{story?.updates?.length > 0 && (
				<>
					{/* Update Tag */}
					<S.StoryUpdatesSignpost id="updates" style={{ top: '-70px' }} />

					{/* Updates */}
					{story?.updates.map((update) => (
						<div key={update.update_id}>
							<S.StoryColumnContainer>

								{/* Timeline Column */}
								<S.StoryTimelineColumn>
									<S.TimelineNodeLabel>
										<Typography
											tag="p"
											variation="2"
											weight="semibold"
											className={isLoading && 'shimmer shimmer-element'}
										>
											{generateStoryUpdatePeriod(update.post_period, t)}
										</Typography>
									</S.TimelineNodeLabel>
									<S.TimelineNode>
										<FontAwesomeIcon icon={['far', 'clock']} />
									</S.TimelineNode>
									<S.TimelineLineCap />
									<S.TimelineLine />
								</S.StoryTimelineColumn>

								{/* Content Column */}
								<S.StoryContentColumn>

									{/* Update Title */}
									{update?.title && (
										<Typography
											tag="h5"
											weight="bold"
											style={{ margin: '0px 0px 8px' }}
											className={isLoading && 'shimmer shimmer-element'}
										>
											<ObfuscatedText>{update?.title}</ObfuscatedText>
										</Typography>
									)}

									{/* Story Content */}
									<StoryContent story={update} isLoading={isLoading} />

								</S.StoryContentColumn>
							</S.StoryColumnContainer>

							{/* Timeline End */}
							<S.StoryColumnContainer style={{ marginBottom: '5px' }}>
								<S.StoryTimelineColumn>
									<S.TimelineLineCap />
								</S.StoryTimelineColumn>
							</S.StoryColumnContainer>
						</div>
					))}
				</>
			)}

			{/* Story Column Container */}
			<S.StoryColumnContainer>

				{/* Timeline Column */}
				<S.StoryTimelineColumn>
					<S.TimelineNodeLabel>
						<Typography
							tag="p"
							variation="2"
							weight="semibold"
							className={isLoading && 'shimmer shimmer-element'}
						>
							<Trans t={t} i18nKey="storyCard.originalStory">
								Original story
							</Trans>
						</Typography>
					</S.TimelineNodeLabel>
					<S.TimelineNode>
						<FontAwesomeIcon icon={['far', 'clock']} />
					</S.TimelineNode>
					<S.TimelineLineCap />
					<S.TimelineLine />
				</S.StoryTimelineColumn>

				{/* Content Column */}
				<S.StoryContentColumn>

					{/* Story Content */}
					<StoryContent story={story} isLoading={isLoading} />

				</S.StoryContentColumn>
			</S.StoryColumnContainer>

			{/* Timeline End */}
			<S.StoryColumnContainer>
				<S.StoryTimelineColumn>
					<S.TimelineLineCap />
				</S.StoryTimelineColumn>
			</S.StoryColumnContainer>

			{/* Navigation Bar */}
			{navigationBar}

			{/* Action Bar */}
			<S.ActionBar>

				{/* Action - React */}
				<FlyToTargetContext
					targets={{ desktop: 'desktopReactionMetric', mobile: 'mobileReactionMetric' }}
					flyElement={<FontAwesomeIcon className="love active" icon={['fas', 'heart']} />}
					navigationState={animationState}
					ref={reactionActionModuleRef}
					refOffsetTop={-5}
					setAnimationDuration={setAnimationDuration}
				>
					<ActionModule
						onClick={handleReaction}
						canFly={!isReactionActive}
						data-tip={t('reactions.reaction_a')}
						data-for={`${story?.story_id}-heart`}
						ref={reactionActionModuleRef}
					>
						<FontAwesomeIcon className={isReactionActive ? 'love active' : 'love'} icon={isReactionActive ? ['fas', 'heart'] : ['far', 'heart']} />
						{isReactionActive && (
							<Typography tag="span">
								<Trans t={t} i18nKey="youAndOthers">
									<Typography className="fontBold">you</Typography>
									{' '}
									<Typography className="fontRegular">and</Typography>
									{' '}
									<Typography className="fontBold">others</Typography>
								</Trans>
							</Typography>
						)}
					</ActionModule>
				</FlyToTargetContext>

				{/* Action - Comment */}
				{story?.comments_allowed && (
					<LocaleLink to={`${location.pathname}#comments`} disableLocale>
						<ActionModule>
							<FontAwesomeIcon className="comment" icon={['far', 'comment']} />
							<Typography tag="span">{formatNumber(story?.comment_count) || '–'}</Typography>
						</ActionModule>
					</LocaleLink>
				)}

				{/* Tooltip */}
				<ToolTip id={`${story?.story_id}-heart`} weight="medium" />

				{/* Spacer */}
				<S.ActionSpacer />

				{/* Options Dropdown */}
				<CardOptionsWidget items={[
					{
						icon: ['far', 'flag'],
						text: t('actions.report'),
						action: reportStory
					}]}
				/>
			</S.ActionBar>

			{/* Comments Container */}
			<S.CommentsContainer>

				{/* Comment Signpost */}
				<S.CommentSignpost id="comments" />

				{/* Show comments if necessary */}
				{story?.comments_allowed ? (
					<>
						{/* Comment Creator */}
						<CommentCreator source={SOURCES.STORY} sourceId={story?.story_id} postCreateActions={(commentObj) => { setPendingComments([...pendingComments, commentObj]); }} />

						{/* Comment Feed */}
						<CommentFeed source={SOURCES.STORY} sourceId={story?.story_id} pendingComments={pendingComments} />
					</>
				) : (
					<S.CommentsDisabledLabel>
						<Typography tag="p" weight="medium" variation="2" center>
							<Trans t={t} i18nKey="pageStory.commentsDisabled">
								Replies are disabled for this story
							</Trans>
						</Typography>
					</S.CommentsDisabledLabel>
				)}
			</S.CommentsContainer>

		</S.Wrapper>
	);
};


/**
 * Configuration
 */

StoryComponent.displayName = 'StoryComponent';
StoryComponent.propTypes = {
	className: PropTypes.string,
	story: PropTypes.shape(),
	isLoading: PropTypes.bool,
	showBackButton: PropTypes.bool,
	navigationBar: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};
StoryComponent.defaultProps = {
	className: null,
	story: {},
	isLoading: false,
	showBackButton: false,
	navigationBar: null
};
